import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getViewLogsListService } from "modules/monitoreo/services/cerberusService";
import { filterCerberusLogs } from "../model/cerberusModel";
const initialFilters = { busqueda: "", status: "todos", ip: "todos" };
export const useCerberus = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialFilters);
  const pending = useRef(null);
  const mounted = useRef(false);
  const revision = useRef(0);
  const loadLogs = useCallback(async () => {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    const request = ++revision.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getViewLogsListService({}, { signal: controller.signal });
      if (!mounted.current || request !== revision.current || result.cancelled) return null;
      if (result.success) {
        setLogs(result.data);
        setLastUpdated(new Date().toISOString());
      } else setError(result.error || "No se pudieron cargar los registros.");
      return result;
    } catch (err) {
      if (!mounted.current || request !== revision.current) return null;
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      if (mounted.current && request === revision.current) {
        pending.current = null;
        setLoading(false);
      }
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    loadLogs();
    return () => {
      mounted.current = false;
      revision.current += 1;
      pending.current?.abort();
    };
  }, [loadLogs]);
  const filtered = useMemo(() => filterCerberusLogs(logs, filtros), [logs, filtros]);
  const stats = useMemo(
    () => ({
      total: filtered.length,
      activos: filtered.filter((row) => row.status === 1).length,
      inactivos: filtered.filter((row) => row.status === 0).length,
      eliminados: filtered.filter((row) => row.status === -1).length,
      desconocidos: filtered.filter((row) => row.status === null).length,
      hosts: new Set(filtered.map((row) => row.hostname).filter(Boolean)).size,
    }),
    [filtered]
  );
  const ipsUnicas = useMemo(
    () => [...new Set(logs.map((row) => row.hostname).filter(Boolean))].sort(),
    [logs]
  );
  const setFiltro = useCallback(
    (key, value) => setFiltros((prev) => ({ ...prev, [key]: value })),
    []
  );
  const clearFilters = useCallback(() => setFiltros(initialFilters), []);
  return {
    logs: filtered,
    todosLogs: logs,
    loading,
    error,
    lastUpdated,
    filtros,
    stats,
    ipsUnicas,
    loadLogs,
    refreshData: loadLogs,
    setFiltro,
    clearFilters,
  };
};
